import { Card } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { BankAccountEntity } from '@common/enums/bankAccountEntity';
import type { IBankAccountDetailsLogGetAllResult } from '@common/interfaces/bankAccountDetailsLogEntry';
import { ManagerAuthorization } from '@common/enums/managerAuthorization';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { BankAccountLogsModal } from './BankAccountLogsModal';
import { ShowBankAccountDetailsLogsButton } from './ShowBankAccountDetailsLogButton';

interface IBankAccountDetailsLogProps {
    bankAccountEntity: BankAccountEntity;
    entityId: string;
}

export function BankAccountDetailsLogs(props: Readonly<IBankAccountDetailsLogProps>) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [bankAccountLogEntries, setBankAccountLogEntries] = useState<IBankAccountDetailsLogGetAllResult>({
        items: [],
        total: 0,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortableFetch = useAbortableFetch();

    assertHasAuthorization([ManagerAuthorization.organizationFinanceData, ManagerAuthorization.employmentFinanceData]);

    const handleOpenModal = async () => {
        // Page needs to be reset to 1 when opening the modal
        const nextCurrentPage = 1;

        setIsModalOpen(true);
        setCurrentPage(nextCurrentPage);

        await fetchBankAccountDetailsLogs(nextCurrentPage, itemsPerPage);
    };

    const handlePaginationChange = async (nextCurrentPage: number, nextItemsPerPage: number) => {
        setCurrentPage(nextCurrentPage);
        setItemsPerPage(nextItemsPerPage);

        await fetchBankAccountDetailsLogs(nextCurrentPage, nextItemsPerPage);
    };

    useEffect(() => {
        fetchBankAccountDetailsLogs(currentPage, itemsPerPage);

        return () => abortableFetch.abort();
    }, [props.entityId]);

    if (!bankAccountLogEntries.total) {
        return null;
    }

    return (
        <Card.Footer>
            <ShowBankAccountDetailsLogsButton onClick={handleOpenModal} />
            <BankAccountLogsModal
                bankAccountLogEntries={bankAccountLogEntries}
                handleClose={() => setIsModalOpen(false)}
                isLoading={isLoading}
                isOpen={isModalOpen}
                pagination={{
                    currentPage,
                    itemsPerPage,
                    onChange: handlePaginationChange,
                }}
            />
        </Card.Footer>
    );

    async function fetchBankAccountDetailsLogs(nextCurrentPage: number, nextItemsPerPage: number) {
        setIsLoading(true);

        const result = await abortableFetch.getJson<IBankAccountDetailsLogGetAllResult>(
            `/api/bankAccountDetailsLogs?entityId=${props.entityId}&bankAccountEntity=${
                props.bankAccountEntity
            }&limit=${nextItemsPerPage}&offset=${(nextCurrentPage - 1) * nextItemsPerPage}`
        );

        setBankAccountLogEntries({
            items: result.json?.items ?? [],
            total: result.json?.total ?? 0,
        });

        setIsLoading(false);
    }
}
